import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";
import * as THREE from "three";
import { envState } from "./env";
import { useStore } from "../store";
import { POS } from "./layout";

const vert = `
varying vec2 vUv;
void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
`;
const frag = `
uniform sampler2D map2001;
uniform sampler2D map2026;
uniform float uMix;
uniform float uRepeat;
uniform float uNight;
uniform vec3 uFog;
varying vec2 vUv;
void main(){
  vec2 uv = vec2(fract(vUv.x * uRepeat), vUv.y);
  vec3 col = mix(texture2D(map2001, uv).rgb, texture2D(map2026, uv).rgb, uMix);
  float a = smoothstep(0.30, 0.44, vUv.y) * (1.0 - smoothstep(0.60, 0.80, vUv.y));
  col = mix(col, uFog, 0.28);
  col *= (1.0 - 0.55 * uNight);
  col = mix(col, uFog, smoothstep(0.44, 0.30, vUv.y));
  gl_FragColor = vec4(col, a);
  if (gl_FragColor.a < 0.01) discard;
}
`;

// 360deg Manhattan skyline ring that temporally swaps 2001 <-> 2026.
export default function Skyline() {
  const [tex01, tex26] = useTexture(["/skyline_2001.jpg", "/skyline_2026.jpg"]);
  useMemo(() => {
    [tex01, tex26].forEach((t) => {
      t.wrapS = THREE.RepeatWrapping;
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 8;
    });
  }, [tex01, tex26]);

  const uniforms = useMemo(
    () => ({
      map2001: { value: tex01 },
      map2026: { value: tex26 },
      uMix: { value: 1 },
      uRepeat: { value: 3.0 },
      uNight: { value: 0 },
      uFog: { value: new THREE.Color("#cfe3ef") },
    }),
    [tex01, tex26]
  );

  useFrame((state) => {
    const t = useStore.getState().temporal;
    uniforms.uMix.value = THREE.MathUtils.clamp((t - 2001) / 25, 0, 1);
    uniforms.uNight.value = envState.night;
    if (state.scene.fog) uniforms.uFog.value.copy(state.scene.fog.color);
  });

  return (
    <mesh position={[POS.northPool[0], 130, POS.northPool[2]]} frustumCulled={false} renderOrder={-0.5}>
      <cylinderGeometry args={[1550, 1550, 1150, 96, 1, true]} />
      <shaderMaterial
        uniforms={uniforms}
        vertexShader={vert}
        fragmentShader={frag}
        side={THREE.BackSide}
        transparent
        depthWrite={false}
        fog={false}
      />
    </mesh>
  );
}
